// Third party library imports
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { lastValueFrom } from 'rxjs';

// Zopup library imports
import {
    BizError,
    TenantUomPreferenceDto,
    TenantUomPreferenceDtoWebApiResponse,
    TenantUomPreferenceUpsertCommand,
    RegionProfileDto,
    RegionProfileDtoWebApiResponse,
    UOMDto,
    BooleanWebApiResponse,
} from '@zopup/zop-types';
import {
    StorageService,
    StorageKey,
    getTenantIdOrThrow,
    extractFirstErrorMessage,
} from '@zopup/zop-shared';

// Internal project imports
import { TenantUomPreferenceService } from './tenant-uom-preference.service';
import { RegionProfileService } from './region-profile.service';
import { UOMRepositoryStore } from './uom.store';

// Cache TTL for preferences (5 minutes)
const PREFERENCE_CACHE_TTL_MS = 5 * 60 * 1000;

export type UomPreferenceState = {
    // Tenant preference
    tenantPreference: TenantUomPreferenceDto | null;
    tenantPreferenceMeta: number;

    // Region profile
    regionProfile: RegionProfileDto | null;
    regionProfileMeta: number;

    // Current context
    currentTenantId: string | null;
    currentRegion: string | null;

    isLoading: boolean;
    errors: BizError[];
};

const initialState: UomPreferenceState = {
    tenantPreference: null,
    tenantPreferenceMeta: 0,
    regionProfile: null,
    regionProfileMeta: 0,
    currentTenantId: null,
    currentRegion: null,
    isLoading: false,
    errors: [],
};

export const UomPreferenceStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((state) => {
        // Inject UOMRepositoryStore for code lookups
        const uomStore = inject(UOMRepositoryStore);

        return {
            // Resolved default UOMs by dimension
            // Hierarchy: TenantPreference → RegionProfile → null
            defaultUomsByDimension: computed(() => {
                const resolved: Record<string, UOMDto | null> = {};
                const dimensions = [
                    'Mass',
                    'Length',
                    'Volume',
                    'Temperature',
                    'Pressure',
                    'Area',
                    'Time',
                    'Count',
                ];

                const tenantPref = state.tenantPreference();
                const regionProf = state.regionProfile();
                const codeIndex = uomStore.codeIndex();
                const entities = uomStore.entities();

                for (const dim of dimensions) {
                    // Try tenant preference first
                    const tenantCode = tenantPref?.defaultUomByDimension?.[dim];
                    if (tenantCode) {
                        const uomId = codeIndex[tenantCode];
                        if (uomId && entities[uomId]) {
                            resolved[dim] = entities[uomId];
                            continue;
                        }
                    }

                    // Fall back to region profile
                    const regionCode = regionProf?.defaultUomByDimension?.[dim];
                    if (regionCode) {
                        const uomId = codeIndex[regionCode];
                        if (uomId && entities[uomId]) {
                            resolved[dim] = entities[uomId];
                            continue;
                        }
                    }

                    // No default found
                    resolved[dim] = null;
                }

                return resolved;
            }),

            // Invoice standard info
            invoiceStandard: computed(() => state.tenantPreference()?.invoiceStandard || null),
            invoiceStandardVersion: computed(
                () => state.tenantPreference()?.invoiceStandardVersion || null
            ),

            // Export standard info
            exportStandard: computed(() => state.tenantPreference()?.exportStandard || null),
            exportStandardVersion: computed(
                () => state.tenantPreference()?.exportStandardVersion || null
            ),
        };
    }),
    withMethods(
        (
            store,
            tenantUomPrefService = inject(TenantUomPreferenceService),
            regionProfileService = inject(RegionProfileService),
            storageService = inject(StorageService)
        ) => {
            // ================================================================
            // INTERNAL HELPERS
            // ================================================================

            const getTenantId = (): string => {
                return getTenantIdOrThrow(storageService);
            };

            const isCacheFresh = (cachedAt: number): boolean => {
                return Date.now() - cachedAt < PREFERENCE_CACHE_TTL_MS;
            };

            // ================================================================
            // CORE METHODS
            // ================================================================

            // Initialize: Load both tenant preference and region profile
            const initialize = async (): Promise<void> => {
                try {
                    patchState(store, { isLoading: true });

                    const tenantId = getTenantId();
                    patchState(store, { currentTenantId: tenantId });

                    // Fetch tenant preference
                    const tenantPref = await fetchTenantPreference();

                    // Fetch region profile if region is available
                    if (tenantPref?.region) {
                        await fetchRegionProfile(tenantPref.region);
                    }

                    patchState(store, { isLoading: false });
                } catch (error) {
                    console.error('Exception in UomPreferenceStore => initialize()');
                    console.error(error);
                    patchState(store, { isLoading: false });
                }
            };

            // Fetch tenant preference with cache
            const fetchTenantPreference = async (
                options?: { force?: boolean }
            ): Promise<TenantUomPreferenceDto | null> => {
                const cached = store.tenantPreference();
                const meta = store.tenantPreferenceMeta();

                if (!options?.force && cached && isCacheFresh(meta)) {
                    return cached;
                }

                try {
                    const tenantId = getTenantId();

                    const response = await lastValueFrom(
                        tenantUomPrefService.tenantUomPreferencesSingleGet(tenantId)
                    );

                    if (!response.isSuccess) {
                        // No preference set for this tenant is okay, return null
                        console.log('[UOM Preferences] No preferences found for tenant (this is normal for new tenants)');
                        patchState(store, {
                            tenantPreference: null,
                            tenantPreferenceMeta: Date.now(),
                        });
                        return null;
                    }

                    const preference = response.result || null;

                    patchState(store, {
                        tenantPreference: preference,
                        tenantPreferenceMeta: Date.now(),
                        currentRegion: preference?.region || null,
                    });

                    console.log('[UOM Preferences] Loaded preferences:', preference?.region || 'default');
                    return preference;
                } catch (error: any) {
                    // Check if it's a 404 (not found) - this is expected for tenants without preferences
                    const is404 = error?.status === 404 || error?.error?.status === 404;
                    if (is404) {
                        console.log('[UOM Preferences] No preferences configured for tenant (404 - expected)');
                    } else {
                        console.error('Exception in UomPreferenceStore => fetchTenantPreference()');
                        console.error(error);
                    }

                    patchState(store, {
                        tenantPreference: null,
                        tenantPreferenceMeta: Date.now(),
                    });
                    return null;
                }
            };

            // Fetch region profile with cache
            const fetchRegionProfile = async (
                region: string,
                options?: { force?: boolean }
            ): Promise<RegionProfileDto | null> => {
                const cached = store.regionProfile();
                const meta = store.regionProfileMeta();
                const currentRegion = store.currentRegion();

                if (
                    !options?.force &&
                    cached &&
                    currentRegion === region &&
                    isCacheFresh(meta)
                ) {
                    return cached;
                }

                try {
                    const response = await lastValueFrom(
                        regionProfileService.regionProfilesRegionGet(region)
                    );

                    if (!response.isSuccess) {
                        throw new Error(
                            extractFirstErrorMessage(response, 'Failed to fetch region profile')
                        );
                    }

                    const profile = response.result || null;

                    patchState(store, {
                        regionProfile: profile,
                        regionProfileMeta: Date.now(),
                        currentRegion: region,
                    });

                    return profile;
                } catch (error) {
                    console.error('Exception in UomPreferenceStore => fetchRegionProfile()');
                    console.error(error);
                    patchState(store, {
                        regionProfile: null,
                        regionProfileMeta: Date.now(),
                    });
                    return null;
                }
            };

            // Update tenant preference
            const updateTenantPreference = async (
                command: TenantUomPreferenceUpsertCommand
            ): Promise<TenantUomPreferenceDtoWebApiResponse> => {
                try {
                    patchState(store, { isLoading: true });
                    const tenantId = getTenantId();

                    const response = await lastValueFrom(
                        tenantUomPrefService.tenantUomPreferencesPut(tenantId, command)
                    );

                    if (response.isSuccess && response.result) {
                        patchState(store, {
                            tenantPreference: response.result,
                            tenantPreferenceMeta: Date.now(),
                            currentRegion: response.result.region || null,
                            isLoading: false,
                        });

                        // If region changed, fetch new region profile
                        if (response.result.region) {
                            await fetchRegionProfile(response.result.region, { force: true });
                        }
                    } else {
                        patchState(store, {
                            errors: response.errors,
                            isLoading: false,
                        });
                    }

                    return response;
                } catch (error) {
                    console.error('Exception in UomPreferenceStore => updateTenantPreference()');
                    console.error(error);
                    patchState(store, { isLoading: false });
                    throw error;
                }
            };

            // Delete tenant preference
            const deleteTenantPreference = async (): Promise<BooleanWebApiResponse> => {
                try {
                    patchState(store, { isLoading: true });
                    const tenantId = getTenantId();

                    const response = await lastValueFrom(
                        tenantUomPrefService.tenantUomPreferencesDelete(tenantId)
                    );

                    if (response.isSuccess) {
                        patchState(store, {
                            tenantPreference: null,
                            tenantPreferenceMeta: 0,
                            isLoading: false,
                        });
                    } else {
                        patchState(store, { isLoading: false });
                    }

                    return response;
                } catch (error) {
                    console.error('Exception in UomPreferenceStore => deleteTenantPreference()');
                    console.error(error);
                    patchState(store, { isLoading: false });
                    throw error;
                }
            };

            // Get preferred UOM code for a dimension
            const getPreferredUomCodeForDimension = (dimension: string): string | null => {
                const tenantPref = store.tenantPreference();
                const regionProf = store.regionProfile();

                // Try tenant preference first
                const tenantCode = tenantPref?.defaultUomByDimension?.[dimension];
                if (tenantCode) return tenantCode;

                // Fall back to region profile
                const regionCode = regionProf?.defaultUomByDimension?.[dimension];
                if (regionCode) return regionCode;

                return null;
            };

            // Invalidate cache (force refresh on next fetch)
            const invalidateCache = (): void => {
                patchState(store, {
                    tenantPreferenceMeta: 0,
                    regionProfileMeta: 0,
                });
            };

            // ================================================================
            // RETURN PUBLIC API
            // ================================================================

            return {
                // Core methods
                initialize,
                fetchTenantPreference,
                fetchRegionProfile,
                updateTenantPreference,
                deleteTenantPreference,

                // Helper methods
                getPreferredUomCodeForDimension,
                invalidateCache,
            };
        }
    )
);
